// ============================================================
// FileList.tsx
// The case's FileAttachment rows with per-row Open / Download / Print /
// Delete. Dumb-rendering — fetching and the post-mutation refetch live in
// the parent (the Documents section), so this component just consumes the
// `files` prop and calls `onFileDeleted` after a successful delete.
//
// Stage 4 (approved mockup casevo-case-sections-batch3.html §1) — restyled
// as the Document-search CARD ROWS (file-type badge · name + type line ·
// uploader · date · size · actions), with a filter bar (filename / type
// segments / group-by) and date-grouped headings with per-group counts.
// BEHAVIOUR IS UNCHANGED:
//   • Open — previews previewable types in a tab, downloads the rest
//     (server-decided via the RESPONSE mime); Download — always attachment;
//     Print — printable types only (hidden iframe); Delete — ConfirmDialogue,
//     then deleteFileApi(caseId, id) → onFileDeleted (parent re-lists)
//   • Delete only for the uploader / an admin / deleteOthersDocuments
//     (useCanDeleteDocument) — others see a lock with the reason
//   • loading / error ("Try again") / empty / list states; actionError
//     banner for per-row failures
//   • NEW: an in-flight upload (FileUpload onPendingChange) renders as a
//     pending row in an "Uploading" group, with Cancel.
//   • (E-sign fixes B) An e-signed copy (file.esigned) shows "E-signed ·
//     Statement SS-…" and is read-only: no Delete (a lock says why); Open /
//     Download / Print / Share work as for any file.
//   • (D12) …and is shown uploaded by "Casevo e-signature" (a pen tile, not a
//     person — uploadedBySystem), "Sent for signing by …" in its detail.
// ============================================================
import { refusalText } from '../../utils/serverError'
import { useCanDeleteDocument } from '../../utils/documentPermissions'
import React, { useMemo, useState } from 'react'
import ConfirmDialogue from './ConfirmDialogue'
import ShareLinksModal from './ShareLinksModal'
import ActionBox, { ErrorNote } from './ActionBox'
import {
  requestDownloadUrlFlatApi,
  deleteFileApi,
  FileAttachment,
} from '../../api/casevoApi'
import { isInlineSafeMime, openUrlInTab, triggerBlobDownload, isPrintableMime, printUrlViaIframe } from '../../utils/documentAccess'
import { useLiveUserName } from '../../hooks/useLiveUserName'
import { useCasevoStore } from '../../store/useCasevoStore'
import { CARD } from '../clientInfo/ClientInfoSections'
import { SKELETON_BAR } from '../../hooks/useLoadPhase'
import UserAvatar from './UserAvatar'
import type { PendingUpload } from './FileUpload'
import EsignedTag, { ESIGNED_LOCKED, ESIGN_UPLOADER, PROOF_LOCKED, ProofTag, SystemUploaderChip } from './EsignedTag'

// (D12) A system upload (an e-signed copy) — never a person's name / avatar.
export const isSystemUpload = (f: Pick<FileAttachment, 'uploadedBySystem' | 'esigned' | 'uploader'>): boolean =>
  f.uploadedBySystem === true || (!!f.esigned && f.uploader?.firstName === ESIGN_UPLOADER)

// ── icons (Feather, currentColor) ────────────────────────────
type DocIconName = 'open' | 'dl' | 'print' | 'trash' | 'lock' | 'x' | 'search' | 'layers' | 'chev' | 'upload' | 'brief' | 'user' | 'link'
const PATHS: Record<DocIconName, React.ReactNode> = {
  open: <><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><path d="M15 3h6v6M10 14L21 3" /></>,
  dl: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5M12 15V3" /></>,
  print: <><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></>,
  trash: <><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></>,
  lock: <><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>,
  x: <path d="M18 6L6 18M6 6l12 12" />,
  search: <><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></>,
  layers: <><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></>,
  chev: <path d="M6 9l6 6 6-6" />,
  upload: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></>,
  brief: <><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V4h8v3" /></>,
  user: <><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 12 0v1" /></>,
  link: <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>,
}
export const DocIcon: React.FC<{ n: DocIconName; size?: number; className?: string }> = ({ n, size = 15, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={`shrink-0 ${className}`}>{PATHS[n]}</svg>
)

// ── file kind + badge (mockup .fb / .f-pdf/.f-doc/.f-img/.f-vid) ──
export type DocKind = 'pdf' | 'img' | 'vid' | 'doc' | 'other'
export const docKindOf = (mime: string, filename: string): DocKind => {
  const m = (mime || '').toLowerCase()
  const ext = filename.split('.').pop()?.toLowerCase() ?? ''
  if (m === 'application/pdf' || ext === 'pdf') return 'pdf'
  if (m.startsWith('image/')) return 'img'
  if (m.startsWith('video/')) return 'vid'
  if (m.includes('word') || m.includes('opendocument.text') || m === 'application/rtf' || ['doc', 'docx', 'rtf', 'odt'].includes(ext)) return 'doc'
  return 'other'
}
const BADGE_CLS: Record<DocKind, string> = {
  pdf: 'bg-[#fdeceb] text-[#c2410c] dark:bg-[rgba(248,113,113,0.14)] dark:text-[#fca5a5]',
  doc: 'bg-[#e7efff] text-[#2563eb] dark:bg-[rgba(96,140,255,0.16)] dark:text-[#93b4ff]',
  img: 'bg-[#dcf5f1] text-[#0f766e] dark:bg-[rgba(45,212,191,0.13)] dark:text-[#5eead4]',
  vid: 'bg-[#efe9fb] text-[#7c3aed] dark:bg-[rgba(167,139,250,0.16)] dark:text-[#c4b5fd]',
  other: 'bg-[#eceff1] text-[#475569] dark:bg-[rgba(148,163,184,0.14)] dark:text-[#cbd5e1]',
}
const badgeLabel = (kind: DocKind, filename: string): string => {
  const ext = filename.includes('.') ? filename.split('.').pop()!.toUpperCase() : ''
  if (ext && ext.length <= 4) return ext === 'JPEG' ? 'JPG' : ext
  return { pdf: 'PDF', doc: 'DOC', img: 'IMG', vid: 'VID', other: 'FILE' }[kind]
}
// 40×46, radius 8, folded corner in the page-ground colour (mockup .fb:after).
export const DocBadge: React.FC<{ mime: string; filename: string; muted?: boolean }> = ({ mime, filename, muted }) => {
  const kind = docKindOf(mime, filename)
  return (
    <span
      aria-hidden="true"
      className={`relative w-[40px] h-[46px] rounded-[8px] grid place-items-center shrink-0 text-[10px] font-extrabold tracking-[0.04em] overflow-hidden ${BADGE_CLS[kind]} ${muted ? 'grayscale' : ''} after:content-[''] after:absolute after:top-0 after:right-0 after:border-solid after:[border-width:0_10px_10px_0] after:border-t-transparent after:border-b-transparent after:border-l-transparent after:border-r-casevo-offwhite after:opacity-90`}
    >
      {badgeLabel(kind, filename)}
    </span>
  )
}
// Friendly type line: "application/pdf · opens in browser" / "Word document · downloads".
export const typeLine = (mime: string): string => {
  const m = (mime || '').toLowerCase()
  const name = m.includes('wordprocessingml') || m === 'application/msword' ? 'Word document'
    : m.includes('spreadsheetml') || m === 'application/vnd.ms-excel' ? 'Excel spreadsheet'
      : m.includes('presentationml') ? 'PowerPoint'
        : m || 'file'
  return `${name} · ${isInlineSafeMime(m) ? 'opens in browser' : 'downloads'}`
}

// Human-readable byte size. Bytes for <1 KiB, then KB / MB / GB — GB is needed
// now that uploads accept large evidence media (video) up to 1 GB.
export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

// ── dates (viewer's LOCAL calendar — same buckets as Document search) ──
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const startOfDay = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
const startOfYesterday = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1).getTime()
const startOfWeek = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), d.getDate() - ((d.getDay() + 6) % 7)).getTime()
const startOfMonth = (d: Date): number => new Date(d.getFullYear(), d.getMonth(), 1).getTime()
export const dateBucket = (iso: string, now: Date): { key: string; title: string; rank: number } => {
  const t = new Date(iso).getTime()
  if (t >= startOfDay(now)) return { key: 'today', title: 'Today', rank: 4e6 }
  if (t >= startOfYesterday(now)) return { key: 'yesterday', title: 'Yesterday', rank: 3e6 }
  if (t >= startOfWeek(now)) return { key: 'week', title: 'This week', rank: 2e6 }
  if (t >= startOfMonth(now)) return { key: 'month', title: 'This month', rank: 1e6 }
  const d = new Date(iso)
  const y = d.getFullYear()
  const m = d.getMonth()
  if (y >= now.getFullYear() - 1) return { key: `m:${y}-${m}`, title: `${MONTHS[m]} ${y}`, rank: y * 12 + m }
  return { key: `y:${y}`, title: String(y), rank: y * 12 - 12 }
}
// Row date column: "4 Oct, 16:42" this month · "24 Sep" this year · "Mar 2025"
// older. Fixed English short months (Chromium's en-GB gives "Sept").
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export const rowDate = (iso: string, now: Date): string => {
  const d = new Date(iso)
  const t = d.getTime()
  if (t >= startOfMonth(now)) return `${d.getDate()} ${MON[d.getMonth()]}, ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
  if (d.getFullYear() === now.getFullYear()) return `${d.getDate()} ${MON[d.getMonth()]}`
  return `${MON[d.getMonth()]} ${d.getFullYear()}`
}

// Prefer the uploader's full name; fall back to email when both
// name parts are empty strings (which the backend allows for a
// pre-profile-filled-in user).
export const formatUploader = (
  uploader: { firstName: string; lastName: string; email: string },
): string => {
  const first = uploader.firstName.trim()
  const last = uploader.lastName.trim()
  const fullName = [first, last].filter(Boolean).join(' ')
  return fullName || uploader.email
}
// Uploader chip (24px avatar + name; "You" for the viewer). The avatar is the
// SHARED photo-aware <Avatar> resolved LIVE by userId — the viewer from
// authUser, colleagues from the firmUsers slice (both patched on a rename /
// recolour / new photo) — exactly like the Activity feed's who-line
// (ClientActivityFeed personOf). Photo when set, else the styled initial on
// their colour; `colour` is only the fallback for a user no longer in
// firmUsers (e.g. deactivated).
export const UploaderChip: React.FC<{ userId: string | null | undefined; fallback: string; colour?: string | null; system?: boolean }> = ({ userId, fallback, colour, system }) => {
  if (system) return <SystemUploaderChip name={fallback || ESIGN_UPLOADER} />
  return <PersonChip userId={userId} fallback={fallback} colour={colour} />
}
const PersonChip: React.FC<{ userId: string | null | undefined; fallback: string; colour?: string | null }> = ({ userId, fallback, colour }) => {
  const liveName = useLiveUserName()
  const authUser = useCasevoStore((s) => s.authUser)
  const isMe = !!userId && userId === authUser?.id
  const name = liveName(userId ?? null, fallback) || fallback
  return (
    <span className="flex items-center gap-2 text-[13px] text-casevo-forest min-w-0">
      {/* The shared UserAvatar (the app-wide rule); `colour` only for a user no longer in the firm list. */}
      <UserAvatar userId={userId} fallbackName={fallback} fallbackLook={{ colour }} size="pill" title={false} />
      <span className="truncate" title={name}>{isMe ? 'You' : name}</span>
    </span>
  )
}

// Row grid (mockup .fr: 44px 1fr 170px 92px 74px 132px, gap 14, pad 11/14).
export const DOC_ROW = 'grid grid-cols-[44px_minmax(0,1fr)_auto] md:grid-cols-[44px_minmax(0,1fr)_170px_92px_74px_164px] gap-[14px] items-center px-[14px] py-[11px] mb-2'
const IB = 'w-[30px] h-[30px] rounded-[8px] grid place-items-center text-casevo-text-soft transition-colors focus:outline-none casevo-focus'
const IB_ON = `${IB} hover:bg-casevo-mint hover:text-casevo-brand dark:hover:text-[#a8e6c5] disabled:opacity-50 disabled:cursor-not-allowed`
const IB_OFF = `${IB} opacity-[0.35] cursor-not-allowed`
// Group heading (mockup .agrp) + its right-aligned count.
export const DocGroupHead: React.FC<{ title: string; sub: React.ReactNode; first?: boolean; subClassName?: string }> = ({ title, sub, first, subClassName }) => (
  <div className={`flex items-baseline gap-[10px] ${first ? 'mt-0' : 'mt-[18px]'} mb-2 mx-[2px] text-[12px] font-bold tracking-[0.14em] uppercase text-casevo-text-soft`}>
    <h3 className="m-0 text-inherit font-inherit">{title}</h3>
    <span className={`ml-auto tracking-normal normal-case text-[12.5px] ${subClassName ?? 'font-semibold text-casevo-forest'}`}>{sub}</span>
  </div>
)

type GroupBy = 'date' | 'type' | 'uploader'
type TypeSeg = 'all' | 'pdf' | 'img' | 'vid' | 'other'
const SEGS: { key: TypeSeg; label: string }[] = [
  { key: 'all', label: 'All' }, { key: 'pdf', label: 'PDF' }, { key: 'img', label: 'Images' }, { key: 'vid', label: 'Video' }, { key: 'other', label: 'Other' },
]
const segOf = (f: FileAttachment): TypeSeg => {
  const k = docKindOf(f.mimeType, f.filename)
  return k === 'doc' ? 'other' : k
}
const KIND_TITLE: Record<TypeSeg, string> = { all: 'All', pdf: 'PDFs', img: 'Images', vid: 'Video', other: 'Other files' }
const PAGE = 20

interface FileListProps {
  caseId: string
  files: FileAttachment[]
  onFileDeleted: () => void
  loading?: boolean
  error?: string | null
  // The in-flight upload (FileUpload onPendingChange), shown as a pending row.
  pending?: PendingUpload | null
  // While loading: hold the skeleton's space but don't show it yet (the first
  // 250ms — hooks/useLoadPhase), so a fast load never flashes a skeleton.
  skeletonHidden?: boolean
}

const FileList: React.FC<FileListProps> = ({
  caseId,
  files,
  onFileDeleted,
  loading = false,
  error = null,
  pending = null,
  skeletonHidden = false,
}) => {
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const canDeleteDoc = useCanDeleteDocument() // (Batch 4) uploader / admin / deleteOthersDocuments
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [printingId, setPrintingId] = useState<string | null>(null)
  // The row whose share links are open (ShareLinksModal).
  const [shareLinksFor, setShareLinksFor] = useState<{ id: string; filename: string } | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  // Filter bar (presentation only — never changes what's stored).
  const [q, setQ] = useState('')
  const [seg, setSeg] = useState<TypeSeg>('all')
  const [groupBy, setGroupBy] = useState<GroupBy>('date')
  const [showAll, setShowAll] = useState(false)
  // ★ Live uploader name by user id (central firmUsers/authUser, patched on
  // rename); the file row's uploader copy is the fallback.
  const liveName = useLiveUserName()

  // OPEN — preview previewable types (PDF/images/text) inline in a new
  // tab; download the rest. Server is the arbiter of inline
  // (isInlineSafeMime just picks the affordance from the row's mime).
  const handleOpen = async (file: FileAttachment): Promise<void> => {
    setActionError(null)
    setDownloadingId(file.id)
    try {
      // Request inline; the SERVER decides (allowlist) and returns the
      // authoritative mimeType. Branch on the RESPONSE mime, not
      // file.mimeType, so the decision never depends on the row field.
      const { downloadUrl, filename, mimeType } = await requestDownloadUrlFlatApi(file.id, true)
      if (isInlineSafeMime(mimeType)) {
        openUrlInTab(downloadUrl)
      } else {
        await triggerBlobDownload(downloadUrl, filename)
      }
    } catch {
      setActionError('Failed to open file. Please try again.')
    } finally {
      setDownloadingId(null)
    }
  }
  // DOWNLOAD — always downloads (attachment), regardless of type.
  // Presigned R2 GET → blob → <a download>; plain fetch (not axios) so
  // the Bearer interceptor doesn't invalidate the presigned signature.
  const handleDownload = async (file: FileAttachment): Promise<void> => {
    setActionError(null)
    setDownloadingId(file.id)
    try {
      const { downloadUrl, filename } = await requestDownloadUrlFlatApi(file.id)
      await triggerBlobDownload(downloadUrl, filename)
    } catch {
      setActionError('Failed to download file. Please try again.')
    } finally {
      setDownloadingId(null)
    }
  }

  // PRINT — printable types only (PDF/images; gated at the button by
  // isPrintableMime). Reuses the existing inline presigned-URL path
  // (requestDownloadUrlFlatApi inline=true), then prints via a hidden iframe
  // (no popup, no stray tab). Mirrors handleOpen's error handling. One file
  // at a time — no batch/merge.
  const handlePrint = async (file: FileAttachment): Promise<void> => {
    setActionError(null)
    setPrintingId(file.id)
    try {
      const { downloadUrl } = await requestDownloadUrlFlatApi(file.id, true)
      await printUrlViaIframe(downloadUrl)
    } catch {
      setActionError('Failed to print file. Please try again.')
    } finally {
      setPrintingId(null)
    }
  }

  const handleDeleteConfirmed = async (fileId: string): Promise<void> => {
    setActionError(null)
    setConfirmDeleteId(null)
    setDeletingId(fileId)
    try {
      await deleteFileApi(caseId, fileId)
      onFileDeleted()
    } catch (err) {
      setActionError(refusalText(err, 'Failed to delete file. Please try again.'))
    } finally {
      setDeletingId(null)
    }
  }

  // The file the modal is asking about. Look it up by id so we
  // can show the filename in the confirmation message.
  const fileBeingConfirmed = confirmDeleteId
    ? files.find((f) => f.id === confirmDeleteId) ?? null
    : null

  // ── derived: counts, filter, groups ──
  const segCount = useMemo(() => {
    const c: Record<TypeSeg, number> = { all: files.length, pdf: 0, img: 0, vid: 0, other: 0 }
    for (const f of files) c[segOf(f)]++
    return c
  }, [files])
  const uploaderLabel = (f: FileAttachment): string => (isSystemUpload(f) ? ESIGN_UPLOADER : liveName(f.uploadedBy, formatUploader(f.uploader)))
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return files.filter((f) => (seg === 'all' || segOf(f) === seg) && (!needle || f.filename.toLowerCase().includes(needle)))
  }, [files, q, seg])
  const now = new Date()
  const groups = useMemo(() => {
    const map = new Map<string, { key: string; title: string; rank: number; items: FileAttachment[] }>()
    const sorted = visible.slice().sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime())
    for (const f of sorted) {
      let k: { key: string; title: string; rank: number }
      if (groupBy === 'type') { const s = segOf(f); k = { key: s, title: KIND_TITLE[s], rank: -SEGS.findIndex((x) => x.key === s) } }
      else if (groupBy === 'uploader') { const n = uploaderLabel(f); k = { key: isSystemUpload(f) ? 'system:esign' : f.uploadedBy, title: n, rank: 0 } }
      else k = dateBucket(f.uploadedAt, new Date())
      let g = map.get(k.key)
      if (!g) { g = { ...k, items: [] }; map.set(k.key, g) }
      g.items.push(f)
    }
    const out = Array.from(map.values())
    return groupBy === 'uploader' ? out.sort((a, b) => a.title.localeCompare(b.title)) : out.sort((a, b) => b.rank - a.rank)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, groupBy, liveName])

  // ── Branch 1: loading ────────────────────────────────────
  // Same-shape card rows (badge · name + type line), not a one-line
  // "Loading…" that the real list then pushes down from.
  if (loading) {
    return (
      <div aria-busy="true" aria-label="Loading files" className={skeletonHidden ? 'invisible' : ''}>
        {[0, 1, 2].map((i) => (
          <div key={i} className={`${CARD} ${DOC_ROW}`} aria-hidden="true">
            <span className="w-[40px] h-[46px] rounded-[8px] bg-casevo-border-soft motion-safe:animate-pulse" />
            <div className="min-w-0">
              <span className="block h-[21.75px] leading-[21.75px]"><span className={`${SKELETON_BAR} h-3 w-56 max-w-full`} /></span>
              <span className="block h-[18px] leading-[18px]"><span className={`${SKELETON_BAR} h-2.5 w-32`} /></span>
            </div>
            <span className="hidden md:block" /><span className="hidden md:block" /><span className="hidden md:block" />
            <span />
          </div>
        ))}
      </div>
    )
  }

  // ── Branch 2: error (prop) ───────────────────────────────
  // "Try again" reuses onFileDeleted — the parent treats it as a
  // generic refetch trigger, same callback both delete-success
  // and retry-after-error use.
  if (error) {
    return (
      <ActionBox alert tone="danger" message={error}
        actions={[{ label: 'Try again', kind: 'link', onClick: () => onFileDeleted() }]} />
    )
  }

  // ── Pending upload row (mockup .fr.pend) ──
  const pendingRow = pending && (
    <>
      <DocGroupHead first title="Uploading" sub="Hidden from others until confirmed" />
      <div className={`${CARD} ${DOC_ROW}`} aria-busy="true">
        <DocBadge mime={pending.mimeType} filename={pending.filename} />
        <div className="min-w-0">
          <b className="block text-[14.5px] font-semibold text-casevo-text-soft truncate">{pending.filename}</b>
          <span className="inline-flex items-center gap-[5px] text-[11px] font-bold px-2 py-[2px] rounded-full bg-[#fdf3dc] text-[#8a6212] dark:bg-[rgba(214,170,80,0.14)] dark:text-[#dcbb72]">
            <DocIcon n="upload" size={12} />
            {pending.phase === 'uploading' ? 'Uploading…' : pending.phase === 'confirming' ? 'Confirming…' : 'Uploaded ✓'}
          </span>
          <div className="h-[5px] rounded-[3px] bg-casevo-border-soft mt-[6px] overflow-hidden" aria-hidden="true">
            {/* fetch() exposes no upload progress, so the bar is indeterminate
                until the PUT lands. */}
            <i className={`block h-full bg-casevo-brand ${pending.phase === 'uploading' ? 'w-1/3 animate-pulse motion-reduce:animate-none' : 'w-full'}`} />
          </div>
        </div>
        <div className="hidden md:block"><UploaderChip userId={useCasevoStore.getState().authUser?.id} fallback="You" /></div>
        <div className="hidden md:block text-[13px] text-casevo-text-soft">Just now</div>
        <div className="hidden md:block text-[13px] text-casevo-text-soft text-right tabular-nums">{formatBytes(pending.sizeBytes)}</div>
        <div className="flex gap-[2px] justify-end">
          <span className={`${IB_OFF} hidden sm:grid`} aria-hidden="true"><DocIcon n="open" /></span>
          <span className={`${IB_OFF} hidden sm:grid`} aria-hidden="true"><DocIcon n="dl" /></span>
          <span className={`${IB_OFF} hidden sm:grid`} aria-hidden="true"><DocIcon n="print" /></span>
          <button type="button" className={IB_ON} onClick={pending.cancel} disabled={pending.phase !== 'uploading'} title="Cancel upload" aria-label={`Cancel uploading ${pending.filename}`}>
            <DocIcon n="x" />
          </button>
        </div>
      </div>
    </>
  )

  // ── Branch 3: empty ──────────────────────────────────────
  if (files.length === 0) {
    return (
      <div>
        {pendingRow}
        {!pending && (
          <div className={`${CARD} px-6 py-10 text-center`}>
            <div className="w-12 h-12 rounded-[13px] bg-casevo-mint text-casevo-brand dark:text-[#a8e6c5] grid place-items-center mx-auto mb-3">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
            </div>
            <h4 className="m-0 text-casevo-forest font-semibold text-[15px] mb-1">No documents yet</h4>
            <p className="m-0 text-[13px] text-casevo-text-soft">Drop a file above, or choose one, to add it to this case.</p>
          </div>
        )}
      </div>
    )
  }

  // ── Branch 4: list ───────────────────────────────────────
  const flat = groups.flatMap((g) => g.items)
  const limit = showAll ? Infinity : PAGE
  const shownIds = new Set(flat.slice(0, limit).map((f) => f.id))
  const hiddenCount = flat.length - shownIds.size
  const filtersOn = q.trim() !== '' || seg !== 'all'

  const renderRow = (file: FileAttachment): React.ReactNode => {
    const isDeleting = deletingId === file.id
    const isDownloading = downloadingId === file.id
    const isPrinting = printingId === file.id
    const canPrint = isPrintableMime(file.mimeType)
    const rowDisabled = isDeleting || isDownloading || isPrinting
    const uploader = uploaderLabel(file)
    const sizeLabel = formatBytes(file.sizeBytes)
    const dateLabel = rowDate(file.uploadedAt, now)
    const deliveredPdf = file.deliveredBillPdf === true
    const esigned = file.esigned ?? null
    const proofFor = file.proofFor ?? null
    const canDelete = !deliveredPdf && !esigned && !proofFor && canDeleteDoc(file.uploadedBy)
    return (
      <div key={file.id} className={`${CARD} ${DOC_ROW} transition-colors hover:border-casevo-mid`} data-esigned-row={esigned ? '' : undefined}>
        <DocBadge mime={file.mimeType} filename={file.filename} />
        <div className="min-w-0">
          <button type="button" onClick={() => void handleOpen(file)} disabled={rowDisabled} title={`Open ${file.filename}`}
            className="block max-w-full text-left text-[14.5px] font-semibold text-casevo-forest truncate hover:underline underline-offset-2 disabled:no-underline focus:outline-none casevo-focus rounded-[3px]">
            {file.filename}
          </button>
          <span className="block text-[12px] text-casevo-text-soft truncate">
            {typeLine(file.mimeType)}
            {/* Mobile: the uploader / date / size columns fold into this line. */}
            <span className="md:hidden"> · {uploader} · {dateLabel} · {sizeLabel}</span>
          </span>
          {esigned && <EsignedTag esigned={esigned} className="mt-1" />}
          {proofFor && <ProofTag label={proofFor} className="mt-1" />}
          {file.status === 'pending' && (
            <span className="inline-flex items-center gap-[5px] mt-1 text-[11px] font-bold px-2 py-[2px] rounded-full bg-[#fdf3dc] text-[#8a6212] dark:bg-[rgba(214,170,80,0.14)] dark:text-[#dcbb72]">
              <DocIcon n="upload" size={12} />Pending · not yet confirmed
            </span>
          )}
        </div>
        <div className="hidden md:block min-w-0"><UploaderChip userId={file.uploadedBy} fallback={uploader} system={isSystemUpload(file)} /></div>
        <div className="hidden md:block text-[13px] text-casevo-text-soft whitespace-nowrap" title={new Date(file.uploadedAt).toLocaleString('en-GB')}>{dateLabel}</div>
        <div className="hidden md:block text-[13px] text-casevo-text-soft text-right tabular-nums whitespace-nowrap">{sizeLabel}</div>
        <div className="flex gap-[2px] justify-end">
          <button type="button" onClick={() => void handleOpen(file)} disabled={rowDisabled} title={isInlineSafeMime(file.mimeType) ? 'Open' : 'Open (downloads)'} aria-label={`Open ${file.filename}`} className={IB_ON}>
            <DocIcon n="open" />
          </button>
          <button type="button" onClick={() => void handleDownload(file)} disabled={rowDisabled} title="Download" aria-label={`Download ${file.filename}`} className={IB_ON}>
            {isDownloading
              ? <span className="w-[14px] h-[14px] rounded-full border-2 border-casevo-mint border-t-casevo-brand animate-spin motion-reduce:animate-none" aria-hidden="true" />
              : <DocIcon n="dl" />}
          </button>
          {canPrint ? (
            <button type="button" onClick={() => void handlePrint(file)} disabled={rowDisabled} title="Print" aria-label={`Print ${file.filename}`} className={IB_ON}>
              <DocIcon n="print" />
            </button>
          ) : (
            <span className={IB_OFF} title="Not printable" aria-hidden="true"><DocIcon n="print" /></span>
          )}
          {/* Secure share links — list / cancel (ShareLinksModal). */}
          <button type="button" onClick={() => setShareLinksFor({ id: file.id, filename: file.filename })} disabled={rowDisabled} title="Share links" aria-label={`Share links for ${file.filename}`} className={IB_ON}>
            <DocIcon n="link" />
          </button>
          {canDelete ? (
            <button type="button" onClick={() => setConfirmDeleteId(file.id)} disabled={rowDisabled} title={isDeleting ? 'Deleting…' : 'Delete'} aria-label={`Delete ${file.filename}`}
              className={`${IB_ON} !text-[#c23b2c] dark:!text-[#f3a094] hover:!bg-[#fbe9e6] dark:hover:!bg-[rgba(243,160,148,0.14)]`}>
              <DocIcon n="trash" />
            </button>
          ) : esigned ? (
            <span className={IB_OFF} role="img" title={ESIGNED_LOCKED} aria-label={`Can't delete: ${ESIGNED_LOCKED}`}>
              <DocIcon n="lock" />
            </span>
          ) : proofFor ? (
            <span className={IB_OFF} role="img" title={PROOF_LOCKED} aria-label={`Can't delete: ${PROOF_LOCKED}`}>
              <DocIcon n="lock" />
            </span>
          ) : deliveredPdf ? (
            <span className={IB_OFF} role="img" title="The delivered bill PDF — the exact document the client received — can’t be deleted"
              aria-label="Can't delete: the delivered bill PDF">
              <DocIcon n="lock" />
            </span>
          ) : (
            <span className={IB_OFF} role="img" title={`Uploaded by ${uploader} — only the uploader, an admin or someone with “Delete others' documents” can delete`}
              aria-label={`Can't delete: uploaded by ${uploader}`}>
              <DocIcon n="lock" />
            </span>
          )}
        </div>
      </div>
    )
  }

  const FLD = 'inline-flex items-center gap-[7px] h-8 px-[10px] rounded-[9px] border border-casevo-border bg-casevo-input text-[13px] text-casevo-forest'
  return (
    <div>
      {actionError && (
        <ErrorNote className="mb-4">{actionError}</ErrorNote>
      )}

      {/* Filter bar (mockup .tbar): filename · type segments · group by. */}
      <div className={`${CARD} flex items-center gap-2 flex-wrap px-3 py-[10px] mb-4`}>
        <label className={`${FLD} flex-1 min-w-[150px]`}>
          <DocIcon n="search" size={14} className="text-casevo-text-soft" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by filename…" aria-label="Filter by filename"
            className="flex-1 min-w-0 bg-transparent border-0 outline-none text-[13px] text-casevo-forest placeholder:text-casevo-text-soft" />
          {q && <button type="button" onClick={() => setQ('')} aria-label="Clear filename filter" className="text-casevo-text-soft hover:text-casevo-forest focus:outline-none casevo-focus rounded-[3px]"><DocIcon n="x" size={13} /></button>}
        </label>
        <div className="inline-flex border border-casevo-border rounded-[10px] overflow-hidden bg-casevo-surface" role="group" aria-label="Filter by type">
          {SEGS.map((s) => {
            const on = seg === s.key
            return (
              <button key={s.key} type="button" aria-pressed={on} onClick={() => setSeg(s.key)}
                className={`inline-flex items-center gap-1.5 h-8 px-3 text-[13px] border-r border-casevo-border-soft last:border-r-0 focus:outline-none casevo-focus ${on ? 'bg-casevo-primary text-white font-semibold' : 'text-casevo-mid hover:bg-casevo-mint/40'}`}>
                {s.label}<span className="text-[11px] opacity-75 tabular-nums">{segCount[s.key]}</span>
              </button>
            )
          })}
        </div>
        <label className={FLD}>
          <DocIcon n="layers" size={14} className="text-casevo-text-soft" />
          <span>Group by:</span>
          <select aria-label="Group by" value={groupBy} onChange={(e) => setGroupBy(e.target.value as GroupBy)}
            className="bg-transparent border-0 outline-none text-[13px] font-bold text-casevo-forest pr-1 cursor-pointer appearance-none">
            <option value="date">Date</option>
            <option value="type">Type</option>
            <option value="uploader">Uploaded by</option>
          </select>
          <DocIcon n="chev" size={12} className="text-casevo-text-soft" />
        </label>
      </div>

      {pendingRow}
      {visible.length === 0 ? (
        <div className={`${CARD} px-6 py-8 text-center`}>
          <p className="m-0 text-[13.5px] text-casevo-text-soft">No files match these filters.</p>
          {filtersOn && <button type="button" onClick={() => { setQ(''); setSeg('all') }} className="mt-2 text-[13px] text-casevo-brand dark:text-[#8fd6b0] hover:underline focus:outline-none casevo-focus rounded-sm">Clear filters</button>}
        </div>
      ) : (
        groups.map((g, i) => {
          const rows = g.items.filter((f) => shownIds.has(f.id))
          if (rows.length === 0) return null
          return (
            <section key={g.key} aria-label={g.title}>
              <DocGroupHead first={i === 0 && !pending} title={g.title} sub={`${g.items.length} file${g.items.length === 1 ? '' : 's'}`} />
              {rows.map(renderRow)}
            </section>
          )
        })
      )}
      {hiddenCount > 0 && (
        <div className="flex justify-center mt-[6px] mb-1">
          <button type="button" onClick={() => setShowAll(true)}
            className="inline-flex items-center gap-[7px] h-[30px] px-[11px] rounded-[8px] text-[12.5px] font-medium border border-casevo-border bg-casevo-surface text-casevo-forest hover:border-casevo-mid focus:outline-none casevo-focus">
            Show {hiddenCount} more
          </button>
        </div>
      )}

      {shareLinksFor && <ShareLinksModal fileId={shareLinksFor.id} filename={shareLinksFor.filename} onClose={() => setShareLinksFor(null)} />}
      <ConfirmDialogue
        isOpen={fileBeingConfirmed !== null}
        title="Delete document?"
        message={
          fileBeingConfirmed
            ? `${fileBeingConfirmed.filename} will be permanently deleted. This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        onConfirm={() => {
          if (fileBeingConfirmed) {
            void handleDeleteConfirmed(fileBeingConfirmed.id)
          }
        }}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </div>
  )
}

export default FileList
